const parseTraceIdForResponse = header => {
  const _map = {};
  if (typeof header === 'string') {
    const _obj = header.split('\n');

    for (const key in _obj) {
      if (Object.prototype.hasOwnProperty.call(_obj, key)) {
        const element = _obj[key].split(':');
        if (element.length === 2) {
          _map[element[0]] = element[1] && element[1].trim();
        }
      }
    }
  }
  return _map['x-eagleeye-id'] || '';
};

const is = type => target => ({}).toString.call(target) === `[object ${type}]`;
const isObject = is('Object');
const isString = is('String');
const isRegExp = is('RegExp');
const isNumber = is('Number');
const isFunction = is('Function');

/**
 * 资源加载类异常处理方法
 */
const getResourceErrorSrc = (target = {} as any) => {
  let src = target.src || target.href;
  // IMG
  if (target.tagName.toLowerCase() === 'img') {
    // 如果是编码图片，则截取图片类型和编码作为src
    if (src.indexOf('data:') === 0) {
      src = src.split(',')[0];
    }
  }
  return src;
};

/**
 * 获取元素XPath
 * @param {HTMLElement} dom DOM元素
 * @param {Number} depth DOM深度，默认查找到根元素
 */
const getXPath = (dom, depth) => {
  if (!dom) {
    return '';
  }

  const { id = '', className = '', tagName = '' } = dom;
  const newTagName = tagName.toLowerCase();
  const hasIdOrClass = id || className;
  const idClassAttr = [];
  if (id) {
    idClassAttr.push(`[@id="${id}"]`);
  }
  if (className) {
    idClassAttr.push(`[@class="${className}"]`);
  }

  const selfXPath = `${newTagName}${hasIdOrClass ? idClassAttr.join('') : ''}`;

  return dom.parentNode && dom.parentNode.tagName && depth - 1 !== 0
    ? `${getXPath(dom.parentNode, depth - 1)}>${selfXPath}`
    : selfXPath;
};

/**
 * 根据规则列表匹配是否忽略，规则可以是字符串、正则表达式或函数。
 * @param {Array<string|RegExp|Function>} rules - 规则列表。
 * @param {string} msg - 需要被测试的字符串。
 * @returns {boolean} 如果有任意一项规则匹配成功，则返回 true，否则返回 false。
 */
const matchIgnore = (rules, msg) => {
  if (!Array.isArray(rules)) {
    return false;
  }

  return rules.some(rule => {
    if (isString(rule)) {
      return rule === msg;
    }
    if (isRegExp(rule)) {
      return rule.test(msg);
    }
    if (isFunction(rule)) {
      return !!rule(msg);
    }
    return false;
  });
};

/**
 * 根据指定的采样率判断是否应该进行采样。
 * @param {number} [sampleRate=1] - 采样率，一个 0 到 1 之间的数字。
 *                                 0.01 表示 1%，1 表示 100%。
 *                                 默认为 1，即 100% 采样。
 * @returns {boolean} 如果命中采样，则返回 true；否则返回 false。
 */
const isSampled = (sampleRate = 1) => {
  // 无效输入，则视为 100% 采样，确保不会丢失数据。
  if (!isNumber(sampleRate)) {
    return true;
  }
  // 如果采样率小于等于 0，永远不采样。
  if (sampleRate <= 0) {
    return false;
  }
  // 如果采样率大于等于 1，永远采样。
  if (sampleRate >= 1) {
    return true;
  }

  // 核心采样逻辑
  return Math.random() < sampleRate;
};

/**
 * 从对象中过滤出可序列化的基础数据类型。
 * @param {object} params - 原始对象。
 * @returns {object} 过滤后的新对象。
 */
const filterSerializableTypes = params => {
  if (!params || typeof params !== 'object') return {};
  const filteredEntries = Object.entries(params).filter(([, value]) => {
    const type = typeof value;
    return type !== 'function' && type !== 'symbol' && type !== 'bigint';
  });
  return Object.fromEntries(filteredEntries);
};

export {
  isObject,
  isString,
  isFunction,
  isSampled,
  getXPath,
  matchIgnore,
  getResourceErrorSrc,
  filterSerializableTypes,
  parseTraceIdForResponse,
};
